// externals
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '@interstate/components/Alert';
import isEqual from 'lodash/isEqual';
import { useFormValidation } from '@makemydeal/dr-dash-components/hooks/useFormValidation';
import { SlideOutWithFixedFooter } from '@makemydeal/dr-dash-components/utils/SlideOutWithFixedFooter';
import { DiscardChangesModal } from '@makemydeal/dr-shared-components';
import {
    providerValidationConfig,
    contractApiErroredVppValidationConfig,
    CONTRACT_API_ERRORED_VPP_ADDRESS_FIELDS
} from '@makemydeal/dr-shared-ui-utils/validationConfigs/vehicleProtectionProductsValidationConfig';

// store
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleProtectionActionCreators from '@makemydeal/dr-dash-store/actions/vehicleProtectionActionCreators';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as vehicleProtectionUtils from '@makemydeal/dr-dash-store/utils/vehicleProtectionUtils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';

// types
import type { DealerProduct, DraftDealProduct, OverriddenTermFields, StateTree } from '@makemydeal/dr-dash-types';
import type { EditProductDrawerProps, EditProductTermsProps } from '../../types';
import type { RatedProtectionProduct } from '@makemydeal/dr-platform-types';
import { DealTabs, GAP } from '@makemydeal/dr-shared-types';

// components
import EditProductAddOns from './EditProductAddOns';
import EditProductInformation from './EditProductInformation';
import EditProductTaxable from './EditProductTaxable';
import EditProductTerms from './EditProductTerms';
import EditProductPlan from './EditProductPlan';
import EditProductPrices from './EditProductPrices';

// utils
import {
    getIncludedAddOnNames,
    updateDealProductWithRates,
    getPriceInputErrorMessage,
    getPlanDefaultRate,
    getComparableProductState,
    restoreOverriddenTermFields
} from '../../utils';
import { GAP_PRODUCT_CATEGORY_CODE } from '../../constants';

const EditProductDrawer = ({ productCode: initialProductCode, onClose }: EditProductDrawerProps) => {
    const [productCode, setProductCode] = useState<string | undefined>(undefined);
    const [selectedProductPlan, setSelectedProductPlan] = useState<string | undefined>(undefined);
    const [showDiscardWarning, setShowDiscardWarning] = useState(false);
    const [isOverrideSwitchOn, setIsOverrideSwitchOn] = useState(false);

    const editableProducts = useSelector(vehicleProtectionSelectors.getVppEditableProducts);
    const dealerProducts = useSelector(vehicleProtectionSelectors.getVppDealerProducts);
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);

    const product = useSelector<StateTree, DraftDealProduct | undefined>((state) =>
        vehicleProtectionSelectors.getVppProduct(state, productCode)
    );

    const initialProduct = useSelector<StateTree, DraftDealProduct | undefined>((state) =>
        vehicleProtectionSelectors.getVppProduct(state, initialProductCode)
    );

    const initialProductOriginalProviderName = useSelector((state: StateTree) =>
        vehicleProtectionSelectors.getVppOriginalProviderName(state, initialProductCode)
    );

    const ratedProduct = useSelector<StateTree, RatedProtectionProduct | undefined>((state) =>
        vehicleProtectionSelectors.getVppRatedProduct(state, productCode)
    );

    const initialRatedProduct = useSelector<StateTree, RatedProtectionProduct | undefined>((state) =>
        vehicleProtectionSelectors.getVppRatedProduct(state, initialProductCode)
    );

    const productRates = useSelector((state: StateTree) => vehicleProtectionSelectors.getVppProductRates(state, productCode));

    const rateAttributes = useSelector<StateTree, RatedProtectionProduct['rateAttributes']>((state) =>
        vehicleProtectionSelectors.getVppProductRateAttributes(state, productCode)
    );

    const overriddenTermFields = useSelector<StateTree, OverriddenTermFields>(
        (state) => vehicleProtectionSelectors.getProductOverriddenTermFields(state, productCode),
        isEqual
    );

    const initialOverriddenTermFields = useSelector<StateTree, OverriddenTermFields>(
        (state) => vehicleProtectionSelectors.getProductOverriddenTermFields(state, initialProductCode),
        isEqual
    );

    const dealerProduct = useSelector<StateTree, Partial<DealerProduct> | undefined>((state) =>
        vehicleProtectionSelectors.getVppDealerProduct(state, productCode)
    );

    const initialDealerProduct = useSelector<StateTree, Partial<DealerProduct> | undefined>((state) =>
        vehicleProtectionSelectors.getVppDealerProduct(state, initialProductCode)
    );

    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);

    const isRateableProduct = ratedProduct?.rateAttributes && ratedProduct.rateAttributes.length > 0;

    const isTermManualMode = isOverrideSwitchOn;

    const dispatch = useDispatch();

    const [editedProduct, setEditedProduct] = useState<DraftDealProduct | undefined>(undefined);
    const [priceInputErrorMessage, setPriceInputErrorMessage] = useState('');

    const [hasProductInformationError, setHasProductInformationError] = useState(false);
    const [hasPriceError, setHasPriceError] = useState(false);
    const [hasTermsError, setHasTermsError] = useState(false);

    const [isEditedProductChanged, setIsEditedProductChanged] = useState(false);

    const seedEditedProduct = useCallback(
        (
            sourceProduct: DraftDealProduct,
            sourceRatedProduct: RatedProtectionProduct | undefined,
            sourceDealerProduct: Partial<DealerProduct> | undefined,
            sourceOverriddenTermFields: OverriddenTermFields
        ): DraftDealProduct => {
            if (sourceRatedProduct) {
                const rate = vehicleProtectionUtils.getSelectedRate(sourceRatedProduct, sourceProduct);
                if (rate) {
                    const synced = updateDealProductWithRates(rate, sourceProduct, sourceDealerProduct);
                    return restoreOverriddenTermFields(synced, sourceProduct, sourceOverriddenTermFields);
                }
            }
            return sourceProduct;
        },
        []
    );

    // Compute the initial edited product state from stable selectors keyed by initialProductCode
    const initialEditedProduct = useMemo(() => {
        if (!initialProduct) return;
        return seedEditedProduct(initialProduct, initialRatedProduct, initialDealerProduct, initialOverriddenTermFields);
    }, [seedEditedProduct, initialProduct, initialRatedProduct, initialDealerProduct, initialOverriddenTermFields]);

    const hasError = hasProductInformationError || hasPriceError || hasTermsError;
    const shouldDisableSubmit = hasError || !isEditedProductChanged;

    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const isGAPSelected = useSelector(vehicleProtectionSelectors.getIsGapProductSelected);
    const contractApiErroredVPP = useSelector(vehicleProtectionSelectors.getContractApiErroredVPP);
    const isApiErroredProduct = editedProduct?.productCode === contractApiErroredVPP?.productCode;

    const { engine: validator, formErrors: validatorFormErrors } = useFormValidation({
        initialConfig: providerValidationConfig,
        lockFlow: !isGAPSelected
    });

    const { engine: apiErrorValidator, formErrors: apiErrorFormErrors } = useFormValidation({
        initialConfig: contractApiErroredVppValidationConfig,
        lockFlow: !isApiErroredProduct
    });
    const hasContractApiValidationError =
        isApiErroredProduct &&
        (apiErrorFormErrors.apiTerms?.hasError ||
            apiErrorFormErrors.apiMiles?.hasError ||
            apiErrorFormErrors.apiProviderName?.hasError ||
            CONTRACT_API_ERRORED_VPP_ADDRESS_FIELDS.some(({ apiKey }) => apiErrorFormErrors[apiKey]?.hasError));
    const hasGapValidationError =
        editedProduct?.productCategoryCode === GAP_PRODUCT_CATEGORY_CODE && (hasProductInformationError || hasTermsError);
    const hasFormValidationErrors = isContractingContext && (hasGapValidationError || hasContractApiValidationError);

    const mergedFormErrors = useMemo(
        () => ({ ...validatorFormErrors, ...apiErrorFormErrors }),
        [validatorFormErrors, apiErrorFormErrors]
    );

    useEffect(() => {
        if (isGCDynamicValidationsEnabled && editedProduct?.productCategoryCode === GAP_PRODUCT_CATEGORY_CODE) {
            if (isContractingContext) {
                dispatch(dealScreenErrorActionCreators.setFieldError(GAP, 'terms', hasTermsError, false, true));
            } else {
                // Clear the error when not in contracting context
                dispatch(dealScreenErrorActionCreators.setFieldError(GAP, 'terms', false, false, true));
            }
        }
    }, [dispatch, isContractingContext, isGCDynamicValidationsEnabled, editedProduct?.productCategoryCode, hasTermsError]);

    useEffect(() => {
        if (contractApiErroredVPP) {
            if (isApiErroredProduct && editedProduct) {
                if (contractApiErroredVPP.hasApiTermsError) {
                    apiErrorValidator.validatePath('apiTerms', editedProduct.productCoverageLength ?? 0);
                }
                if (contractApiErroredVPP.hasApiMilesError) {
                    apiErrorValidator.validatePath('apiMiles', editedProduct.productCoverageMiles ?? 0);
                }
                if (contractApiErroredVPP.hasApiProviderNameError) {
                    apiErrorValidator.validatePath('apiProviderName', editedProduct.productProviderName?.trim() ?? '');
                }
                CONTRACT_API_ERRORED_VPP_ADDRESS_FIELDS.forEach(({ hasApiErrorKey, apiKey, sourceKey }) => {
                    if (contractApiErroredVPP[hasApiErrorKey]) {
                        apiErrorValidator.validatePath(apiKey, editedProduct.productProviderAddress?.[sourceKey] ?? '');
                    }
                });
            } else {
                apiErrorValidator.clearErrors();
            }
        }
    }, [isApiErroredProduct, editedProduct, contractApiErroredVPP]);

    useEffect(() => {
        if (product) {
            setEditedProduct(seedEditedProduct(product, ratedProduct, dealerProduct, overriddenTermFields));
        } else {
            setEditedProduct(undefined);
        }
    }, [product, ratedProduct, dealerProduct, overriddenTermFields, seedEditedProduct]);

    useEffect(() => {
        if (!selectedProductPlan || !product || !ratedProduct) {
            return;
        }

        const defaultRateForSelectedPlan = getPlanDefaultRate(ratedProduct.rates, selectedProductPlan);

        if (defaultRateForSelectedPlan) {
            // it's required to reset the product markup to take the original one from the rate
            const draftProductForUpdate = { ...product, productMarkup: undefined };
            const editedProduct = updateDealProductWithRates(defaultRateForSelectedPlan, draftProductForUpdate, dealerProduct);
            setEditedProduct(editedProduct);
        }

        setSelectedProductPlan(undefined);
    }, [product, ratedProduct, dealerProduct, selectedProductPlan]);

    // Determine if the edited product has actually changed from its initial state
    useEffect(() => {
        if (!editedProduct || !initialEditedProduct) {
            return;
        }
        setIsEditedProductChanged(
            !isEqual(getComparableProductState(editedProduct), getComparableProductState(initialEditedProduct))
        );
    }, [editedProduct, initialEditedProduct]);

    const handleDrawerClose = useCallback(() => {
        setIsEditedProductChanged(false);
        setShowDiscardWarning(false);
        onClose();
    }, [onClose]);

    const applyProductChanges = () => {
        if (editedProduct) {
            dispatch(vehicleProtectionActionCreators.updateVppProduct(editedProduct, { skipPayment: true }));

            if (initialProductCode !== productCode) {
                if (
                    initialProduct &&
                    initialProductOriginalProviderName &&
                    initialProduct.productProviderName !== initialProductOriginalProviderName
                ) {
                    dispatch(
                        vehicleProtectionActionCreators.updateVppProduct(
                            { ...initialProduct, productProviderName: initialProductOriginalProviderName },
                            { skipPayment: true }
                        )
                    );
                }

                dispatch(vehicleProtectionActionCreators.unselectVppProduct(initialProductCode as string, { skipPayment: true }));
            }

            /*
             * NOTE: only last action should dispatch payment call, because we expect a transaction like
             * product updated -> initial product unselected -> another product of same provider is selected
             * -> now right time to make a payment call
             */
            dispatch(vehicleProtectionActionCreators.selectVppProduct(productCode as string));

            if (isGCDynamicValidationsEnabled) {
                const errorState = {
                    streetAddress: false,
                    city: false,
                    zip: false,
                    state: false,
                    terms: false
                };
                dispatch(dealScreenErrorActionCreators.setErrorFields(GAP, errorState));
                dispatch(dealScreenErrorActionCreators.setErrorFields(DealTabs.PROTECTION, errorState));
            }

            if (isContractingContext) {
                dispatch(contractingActionCreators.refreshContractValidationErrors());
            }
        }

        setEditedProduct(undefined);
    };

    const onApply = () => {
        applyProductChanges();
        handleDrawerClose();
    };

    const onCancel = useCallback(() => {
        if (isEditedProductChanged) {
            setShowDiscardWarning(true);
            return;
        }
        setEditedProduct(undefined);
        handleDrawerClose();
    }, [handleDrawerClose, isEditedProductChanged]);

    const handleGoBack = () => {
        setShowDiscardWarning(false);
    };

    const handleDiscardChanges = () => {
        setEditedProduct(undefined);
        handleDrawerClose();
    };

    const onProductEdit = useCallback(
        (product: DraftDealProduct) => {
            setEditedProduct((prev) => {
                const editedProduct = { ...prev, ...product };
                const rate = vehicleProtectionUtils.getSelectedRate(ratedProduct, editedProduct);

                /**
                 * NOTE: for now this callback is called only for products with associated rate, so rate should be presented
                 * product without rate isn't editable, so no way to test this behaviour
                 * however better safe than sorry
                 */
                // istanbul ignore next
                if (!rate) {
                    return editedProduct;
                }

                return updateDealProductWithRates(rate, editedProduct, dealerProduct);
            });
        },
        [ratedProduct, dealerProduct]
    );

    // NOTE: wrapper of `onProductEdit` to reset product markup  and later take the original one (before possible user changes)
    const onProductTermsEdit = (productTerms: EditProductTermsProps) => {
        // In manual override mode the typed values must be stored verbatim, bypassing rate-based syncing that would
        // otherwise overwrite them whenever a typed value happens to match an existing rate option. The markup is
        // preserved here (unlike the rate-synced path below) because no rate recompute follows to restore it, so
        // clearing it would zero out a user's existing markup.
        if (isTermManualMode) {
            setEditedProduct((prev) => ({ ...prev, ...productTerms }));
            return;
        }
        onProductEdit({
            ...productTerms,
            productMarkup: undefined
        });
    };

    const handleOverrideToggle = useCallback(
        (enabled: boolean) => {
            // Children emit onOverrideToggle(false) to sync their initial state, so only a genuine on->off transition
            // in manual mode reverts the rate-driven term fields. The revert runs synchronously (same render as the
            // mode change) so the dropdowns never momentarily render the overridden value and flash a validation error.
            const shouldRevert = isOverrideSwitchOn && !enabled;
            setIsOverrideSwitchOn(enabled);
            if (!shouldRevert) return;

            setEditedProduct((current) => {
                // istanbul ignore next - defensive: the override toggle only renders while a product is present
                if (!current) return current;
                if (!ratedProduct) return current;
                const rate =
                    vehicleProtectionUtils.getSelectedRate(ratedProduct, current) ??
                    (current.productProviderPlan ? getPlanDefaultRate(ratedProduct.rates, current.productProviderPlan) : undefined);
                if (!rate) return current;
                // Reset productServiceInterval explicitly: updateDealProductWithRates only sets it when the rate
                // provides one, so an overridden interval would otherwise linger.
                return {
                    ...updateDealProductWithRates(rate, current, dealerProduct),
                    productServiceInterval: rate.productServiceInterval
                };
            });
        },
        [isOverrideSwitchOn, ratedProduct, dealerProduct]
    );

    const handleProviderChange = useCallback(
        (productCategoryCode: string, productProviderName: string) => {
            // NOTE: for now we take first product
            const [providerProduct] = editableProducts.filter((product) => {
                if (product.productCategoryCode !== productCategoryCode) return false;
                // Dropdown exposes original provider names, so match against the dealer's original name
                // to ignore any in-state overrides on the product's current `productProviderName`.
                const dealerProduct = dealerProducts.find((dp) => dp.productCode === product.productCode);
                return dealerProduct?.dealerSettings?.providerName === productProviderName;
            });

            // NOTE: when editedProduct is undefined - the flow won't achieve this place
            // istanbul ignore next
            if (providerProduct?.productCode && editedProduct?.productCode !== providerProduct.productCode) {
                setEditedProduct(providerProduct);
                setProductCode(providerProduct.productCode);
            } else if (editedProduct) {
                setEditedProduct((prev) => (prev ? { ...prev, productProviderName } : prev));
            }
        },
        [editableProducts, dealerProducts, editedProduct]
    );

    const handleProductOrPlanChange = useCallback(
        (productCode: string, productProviderPlan: string) => {
            setSelectedProductPlan(productProviderPlan);
            setProductCode(productCode);
        },
        [setProductCode, setSelectedProductPlan]
    );

    useEffect(() => {
        const message = getPriceInputErrorMessage(editedProduct);
        setPriceInputErrorMessage(message || '');
        setHasPriceError(!!message);
    }, [editedProduct]);

    useEffect(() => {
        setProductCode(initialProductCode);
    }, [initialProductCode]);

    const drawerContentItems: ReactElement[] = [];

    if (editedProduct) {
        drawerContentItems.push(
            <EditProductInformation
                data-testid="edit-product-info"
                productProps={{
                    productCode: editedProduct.productCode,
                    productCategoryCode: editedProduct.productCategoryCode,
                    productName: editedProduct.productName,
                    productInitialCost: editedProduct.productInitialCost,
                    productProviderName: editedProduct.productProviderName,
                    includedAddOnNames: getIncludedAddOnNames(editedProduct.productAddOnDetails),
                    productProviderAddress: editedProduct.productProviderAddress
                }}
                onProviderChange={(productProviderName: string) => {
                    if (editedProduct.productCategoryCode) {
                        handleProviderChange(editedProduct.productCategoryCode, productProviderName);
                    }
                }}
                onProviderAddressChange={(address: DraftDealProduct['productProviderAddress']) => {
                    onProductEdit({
                        ...editedProduct,
                        productProviderAddress: address
                    });
                }}
                validator={validator}
                formErrors={mergedFormErrors}
                onValidationError={setHasProductInformationError}
                onOverrideToggle={handleOverrideToggle}
            />,
            <EditProductPlan
                onPlanEdit={handleProductOrPlanChange}
                productProps={{
                    productCode: editedProduct.productCode,
                    productProviderPlan: editedProduct.productProviderPlan,
                    productCategoryCode: editedProduct.productCategoryCode
                }}
            />
        );

        if (isRateableProduct) {
            if (productRates) {
                drawerContentItems.push(
                    <EditProductTerms
                        data-testid="edit-product-terms"
                        productProps={{
                            productProviderPlan: editedProduct.productProviderPlan,
                            productCoverageLength: editedProduct.productCoverageLength,
                            productCoverageMiles: editedProduct.productCoverageMiles,
                            productDeductible: editedProduct.productDeductible,
                            productDeductibleMethod: editedProduct.productDeductibleMethod,
                            productServiceInterval: editedProduct.productServiceInterval
                        }}
                        productRates={productRates}
                        rateAttributes={rateAttributes}
                        onEdit={onProductTermsEdit}
                        validator={validator}
                        formErrors={mergedFormErrors}
                        onValidationError={setHasTermsError}
                        isProductTaxable={editedProduct.isProductTaxable}
                        isManualMode={isTermManualMode}
                    />
                );
            } else {
                drawerContentItems.push(
                    <EditProductTaxable
                        productProps={{
                            isProductTaxable: editedProduct.isProductTaxable
                        }}
                    />
                );
            }

            drawerContentItems.push(
                <EditProductAddOns productAddOnDetails={editedProduct.productAddOnDetails} onEdit={onProductEdit} />
            );
        } else if (isTermManualMode) {
            // Non-rated products expose the manual term fields only when both the toggle and the override switch are on.
            drawerContentItems.push(
                <EditProductTerms
                    data-testid="edit-product-terms"
                    productProps={{
                        productProviderPlan: editedProduct.productProviderPlan,
                        productCoverageLength: editedProduct.productCoverageLength,
                        productCoverageMiles: editedProduct.productCoverageMiles,
                        productDeductible: editedProduct.productDeductible,
                        productDeductibleMethod: editedProduct.productDeductibleMethod,
                        productServiceInterval: editedProduct.productServiceInterval
                    }}
                    productRates={productRates || []}
                    rateAttributes={rateAttributes}
                    onEdit={onProductTermsEdit}
                    validator={validator}
                    formErrors={mergedFormErrors}
                    onValidationError={setHasTermsError}
                    isProductTaxable={editedProduct.isProductTaxable}
                    isManualMode={true}
                />
            );
        }
        drawerContentItems.push(
            <EditProductPrices
                data-testid="edit-product-prices"
                productProps={{
                    productCode: editedProduct.productCode,
                    productCost: editedProduct.productCost,
                    productPrice: editedProduct.productPrice,
                    profitLocation: editedProduct.profitLocation,
                    minPriceAllowedByLender: editedProduct.minPriceAllowedByLender,
                    maxPriceAllowedByLender: editedProduct.maxPriceAllowedByLender,
                    isProductPriceCapitalized: editedProduct.isProductPriceCapitalized,
                    productMarkup: editedProduct.productMarkup
                }}
                priceInputErrorMessage={priceInputErrorMessage}
                offerType={currentOfferType}
                onEdit={onProductEdit}
            />
        );
    }

    return (
        <>
            <SlideOutWithFixedFooter
                data-testid={'edit-vehicle-protection-product'}
                show={!!productCode}
                onHide={onCancel}
                position="right"
                header={`Edit ${editedProduct?.productCategoryCode} ${!isRateableProduct ? '(non-rated product)' : ''}`}
                panelWidth="min(846px, 95vw)"
                footer={{
                    layout: 'rightAligned',
                    options: [
                        {
                            action: onCancel,
                            label: 'Cancel',
                            customIdentifiers: {
                                optionButton0: {
                                    dataTestId: 'edit-product-drawer-cancel-btn'
                                }
                            }
                        }
                    ],
                    primary: {
                        action: onApply,
                        label: 'Save',
                        disabled: shouldDisableSubmit,
                        customIdentifiers: {
                            primaryButton: {
                                dataTestId: 'edit-product-drawer-apply-btn'
                            }
                        }
                    }
                }}
            >
                {hasFormValidationErrors && (
                    <Alert type="error" sx={{ margin: '1rem 0' }}>
                        Cannot generate contract. Please fix the highlighted Protection details before continuing.
                    </Alert>
                )}
                {drawerContentItems}
            </SlideOutWithFixedFooter>
            <DiscardChangesModal
                show={showDiscardWarning}
                onDiscard={handleDiscardChanges}
                onGoBack={handleGoBack}
                hasValidationErrors={hasError}
            />
        </>
    );
};

export default EditProductDrawer;
